import React, { memo } from 'react';
import { useIntl } from 'react-intl';

import { BaseItem } from '../../../types/Item';
import { SharedDescriptionListProps } from '../../../types/SharedDescriptionListProps';
import { Actions } from '../../Actions/Actions';
import { DescriptionList } from '../../DescriptionList/DescriptionList';
import { Layout } from '../../Layout/Layout';
import { Accordion } from '../Accordion';

type DescriptionListProps = React.ComponentProps<typeof DescriptionList>;
interface Item extends BaseItem {
  terms: DescriptionListProps['terms'];
  isExpanded?: boolean;
  edit?: {
    isEditing: boolean;
    onClick: () => void;
    onSave: () => void;
    onCancel: () => void;
    isSaving: boolean;
  };
}

interface Props extends SharedDescriptionListProps {
  /** Accordion items of description list terms */
  items: Item[];
  /** If passed renders component to be embedded in a parent component. */
  isEmbedded?: boolean;
  search?: string;
  onToggle?: (isExpanded: boolean, item: BaseItem) => void;
  isIndented?: boolean;
}

export const DescriptionListAccordion = memo(
  ({
    isEmbedded,
    items,
    search,
    onToggle,
    mode,
    isIndented = true,
    ...restDescriptionListProps
  }: Props) => {
    const intl = useIntl();
    const mappedItems = items.map(({ title, terms, isExpanded, edit }) => ({
      title,
      content: (
        <Layout direction="column" spacing={4}>
          <DescriptionList
            {...restDescriptionListProps}
            terms={terms}
            search={search}
            mode={mode}
          />
          {edit?.isEditing && (
            <Layout justify="end">
              <Actions
                actions={[
                  {
                    text: intl.formatMessage({
                      id: 'DescriptionListAccordion.cancelButton',
                      defaultMessage: 'Cancel',
                      description: 'Cancel button text',
                    }),
                    level: 'secondary',
                    onClick: edit?.onCancel,
                    disabled: edit?.isSaving,
                  },
                  {
                    text: intl.formatMessage({
                      id: 'DescriptionListAccordion.saveButton',
                      defaultMessage: 'Save',
                      description: 'Save button text',
                    }),
                    level: 'primary',
                    onClick: edit?.onSave,
                    isLoading: edit?.isSaving,
                  },
                ]}
              />
            </Layout>
          )}
        </Layout>
      ),
      isExpanded,
      edit,
    }));

    return (
      <Accordion
        isIndented={isIndented}
        items={mappedItems}
        onToggle={onToggle}
      />
    );
  },
);
